import {t} from '@lingui/macro';

import {isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {RuleBundleTypesEnum} from '../../models/api/ruleCommonTypes';
import {RuleModelStrict} from '../../models/api/ruleModel';
import {Localizer} from '../../store/stateless/internationalizationSelectors';
import {delayToDayHHMM} from '../editors/commons/paramModel';
import {initialCsatState} from './csatRuleHelpers';
import {RuleParametersDependencies} from './ruleConditionModel';
import {humanDuration, isWithinBH} from './ruleHelpers';
import {initialSlaState} from './slaRuleHelpers';
import {toDelayParameter} from './slaRuleModel';

export function computeSlaDescription(
  slaRules: ReadonlyArray<RuleModelStrict>,
  dependencies: RuleParametersDependencies,
  localize: Localizer,
): string {
  const slaState = initialSlaState({slaRules});

  const firstCondition = slaState.conditions?.[0]?.[0];

  if (firstCondition?.type !== 'inbox_in') {
    return localize(t('sla-description-default')`SLA workflow`);
  }

  const inboxIds = firstCondition.params.inboxes;
  const inboxString = computeInboxesDescription(inboxIds, dependencies, localize);

  const breachDelay = slaState.timeLimits.breach;
  const delay = toDelayParameter(breachDelay.delay);

  let durationString = '';
  if (delay) {
    const hhmm = delayToDayHHMM(delay, 'days');
    durationString = isWithinBH(breachDelay)
      ? `${localize(t('sla-duration-business-hours')`SLA during business hours:`)} ${humanDuration(hhmm)}`
      : `${localize(t('sla-duration-all-hours')`SLA:`)} ${humanDuration(hhmm)}`;
  }

  return `${localize(t('sla-description-compound')`SLA workflow for`)} ${inboxString}. ${durationString}`;
}

export function computeCsatDescription(
  csatRules: ReadonlyArray<RuleModelStrict>,
  dependencies: RuleParametersDependencies,
  localize: Localizer,
): string {
  const csatState = initialCsatState({csatRules});

  const firstCondition = csatState.conditions?.[0]?.[0];

  if (firstCondition?.type !== 'inbox_in') {
    return localize(t('csat-description-default')`CSAT survey response`);
  }

  const inboxIds = firstCondition.params.inboxes ?? [];
  const inboxString = computeInboxesDescription(inboxIds, dependencies, localize);

  return `${localize(t('csat-description-inboxes')`CSAT survey response for`)} ${inboxString}`;
}

export function computeRequiredTaggingDescription(localize: Localizer): string {
  return localize(t('required-tagging-description')`Require tag`);
}

function computeInboxesDescription(
  inboxIds: ReadonlyArray<number> | null,
  dependencies: RuleParametersDependencies,
  localize: Localizer,
): string {
  if (inboxIds?.length === 0) {
    return `${localize(t('inboxes-count-description-any')`any inbox`)}`;
  }

  const inboxesNames = inboxIds
    ?.map((inboxId) => dependencies.inboxes.find((i) => i.id === inboxId))
    .filter(isDefined)
    .slice(0, 2)
    .map((inbox) => inbox.name);

  const hiddenInboxesCount = inboxIds ? Math.max(0, inboxIds.length - 2) : 0;
  let hiddenInboxString = '';

  if (hiddenInboxesCount > 1) {
    hiddenInboxString = ` ${localize(
      t('inboxes-count-description-compound')`and`,
    )} ${hiddenInboxesCount} ${localize(t('inboxes-count-others')`others`)}`;
  }

  if (hiddenInboxesCount === 1) {
    hiddenInboxString = localize(t('inboxes-count-one-other')` and 1 other`);
  }

  return `${localize(t('inboxes-count-description-base')`inbox`)} ${inboxesNames?.join(
    ', ',
  )}${hiddenInboxString}`;
}

export function buildRuleDescription(
  bundleType: RuleBundleTypesEnum,
  rules: ReadonlyArray<RuleModelStrict>,
  dependencies: RuleParametersDependencies,
  localize: Localizer,
): string | undefined {
  switch (bundleType) {
    case RuleBundleTypesEnum.REQUIRED_TAGGING:
      return computeRequiredTaggingDescription(localize);

    case RuleBundleTypesEnum.CSAT:
      return computeCsatDescription(rules, dependencies, localize);

    case RuleBundleTypesEnum.SLA:
      return computeSlaDescription(rules, dependencies, localize);

    default:
      return rules[0]?.description;
  }
}
